import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { api } from '../api';

// Admin page for managing ads (annonser).
//
// Each ad has:
//   - title (internal label, not shown to visitors)
//   - image (16:9, uploaded as a file or pasted as a URL)
//   - link (OPTIONAL URL visitors go to when they click the ad — if omitted
//     the ad renders as a non-clickable display)
//   - priority (higher = shown more often in the weighted random pick)
//   - active (whether the ad is eligible to be displayed at all)
function AdsAdmin() {
  const [ads, setAds] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [status, setStatus] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    imageUrl: '',
    linkUrl: '',
    priority: 1,
    active: true
  });
  const fileInputRef = useRef(null);

  useEffect(() => { fetchAds(); }, []);

  const fetchAds = async () => {
    try {
      const res = await api('/api/ads/all');
      if (res.ok) {
        const data = await res.json();
        setAds(data);
      }
    } catch (err) {
      console.error('Kunne ikke hente annonser:', err);
    }
  };

  const resetForm = () => {
    setEditingId(null);
    setFormData({ title: '', imageUrl: '', linkUrl: '', priority: 1, active: true });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setStatus({ type: 'error', message: 'Filen må være et bilde.' });
      return;
    }
    // 5 MB safety cap — bigger than typical 16:9 JPEG, smaller than the
    // 10 MB server body limit.
    if (file.size > 5 * 1024 * 1024) {
      setStatus({ type: 'error', message: 'Bildet er for stort (maks 5 MB).' });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setFormData(prev => ({ ...prev, imageUrl: reader.result }));
    };
    reader.onerror = () => setStatus({ type: 'error', message: 'Kunne ikke lese bildet.' });
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus(null);
    if (!formData.title.trim() || !formData.imageUrl) {
      setStatus({ type: 'error', message: 'Fyll inn tittel og bilde.' });
      return;
    }
    setSaving(true);
    try {
      const endpoint = editingId ? `/api/ads/${editingId}` : '/api/ads';
      const method = editingId ? 'PUT' : 'POST';
      const res = await api(endpoint, {
        method,
        body: JSON.stringify({
          title: formData.title.trim(),
          imageUrl: formData.imageUrl,
          linkUrl: (formData.linkUrl || '').trim(),
          priority: parseInt(formData.priority, 10) || 0,
          active: !!formData.active
        })
      });
      if (res.ok) {
        setStatus({ type: 'success', message: editingId ? 'Annonse oppdatert!' : 'Annonse lagret!' });
        resetForm();
        fetchAds();
      } else {
        const err = await res.json().catch(() => ({}));
        setStatus({ type: 'error', message: err.error || 'Noe gikk galt' });
      }
    } catch (err) {
      setStatus({ type: 'error', message: 'Kunne ikke koble til serveren.' });
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (ad) => {
    setEditingId(ad.id);
    setFormData({
      title: ad.title,
      imageUrl: ad.imageUrl,
      linkUrl: ad.linkUrl || '',
      priority: ad.priority,
      active: ad.active
    });
    if (fileInputRef.current) fileInputRef.current.value = '';
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Er du sikker på at du vil slette denne annonsen?')) return;
    try {
      const res = await api(`/api/ads/${id}`, { method: 'DELETE' });
      if (res.ok) {
        setStatus({ type: 'success', message: 'Annonse slettet!' });
        if (editingId === id) resetForm();
        fetchAds();
      }
    } catch {
      setStatus({ type: 'error', message: 'Kunne ikke slette annonsen' });
    }
  };

  const toggleActive = async (ad) => {
    try {
      const res = await api(`/api/ads/${ad.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          title: ad.title,
          imageUrl: ad.imageUrl,
          linkUrl: ad.linkUrl,
          priority: ad.priority,
          active: !ad.active
        })
      });
      if (res.ok) fetchAds();
    } catch {}
  };

  const inputClass = 'w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-xl text-gray-200 placeholder-gray-500 focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500 transition-colors';
  const labelClass = 'block text-sm font-medium text-gray-300 mb-1.5';

  return (
    <div className="min-h-screen bg-slate-900 pt-8 pb-16 px-4">
      <SEO title="Annonser" path="/admin/annonser" noindex />
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold text-white">Annonser</h1>
          <div className="flex gap-3">
            <Link to="/admin/meny" className="px-4 py-2 bg-slate-800 text-gray-300 rounded-xl hover:bg-slate-700 transition-colors text-sm">
              Admin-meny
            </Link>
          </div>
        </div>

        {status && (
          <div className={`mb-6 p-4 rounded-xl text-sm font-medium ${
            status.type === 'success'
              ? 'bg-emerald-500 bg-opacity-20 text-emerald-400 border border-emerald-500 border-opacity-30'
              : 'bg-red-500 bg-opacity-20 text-red-400 border border-red-500 border-opacity-30'
          }`}>
            {status.message}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-slate-800 bg-opacity-60 border border-slate-700 rounded-2xl p-6 sm:p-8 mb-8">
          <h2 className="text-xl font-bold text-white mb-6">
            {editingId ? 'Rediger annonse' : 'Ny annonse'}
          </h2>

          <div className="space-y-5">
            <div>
              <label className={labelClass}>Tittel *</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="Intern tittel (vises også under annonsen)"
                required
                className={inputClass}
              />
              <p className="mt-1 text-xs text-gray-500">Kort overskrift som vises under annonsebildet.</p>
            </div>

            <div>
              <label className={labelClass}>Lenke (URL)</label>
              <input
                type="url"
                name="linkUrl"
                value={formData.linkUrl}
                onChange={handleChange}
                placeholder="https://annonsor.no/kampanje (valgfritt)"
                className={inputClass}
              />
              <p className="mt-1 text-xs text-gray-500">Valgfritt. Hvis fylt inn, sendes brukeren hit ved klikk (åpnes i ny fane). La stå tom for en annonse som bare vises.</p>
            </div>

            <div>
              <label className={labelClass}>Bilde (16:9) *</label>
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFile}
                  className="flex-1 text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-teal-500 file:text-slate-900 hover:file:bg-teal-400 file:cursor-pointer cursor-pointer"
                />
                <span className="text-xs text-gray-500 self-center">eller lim inn URL under</span>
              </div>
              <input
                type="url"
                name="imageUrl"
                value={formData.imageUrl.startsWith('data:') ? '' : formData.imageUrl}
                onChange={handleChange}
                placeholder="https://bilde.no/annonse.jpg"
                className={inputClass + ' mt-3'}
              />
              {formData.imageUrl && (
                <div className="mt-3">
                  <div className="relative w-full aspect-video bg-slate-700 rounded-xl overflow-hidden">
                    <img
                      src={formData.imageUrl}
                      alt="Forhåndsvisning"
                      className="w-full h-full object-cover"
                      onError={e => { e.target.style.display = 'none'; }}
                    />
                    <span className="absolute top-2 left-2 px-2.5 py-1 rounded-md text-[11px] font-bold uppercase tracking-wider bg-amber-400 text-slate-900 shadow-md">
                      Annonse
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Bildet beskjæres til 16:9. Anbefalt størrelse: 1600×900 eller større.
                  </p>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className={labelClass}>Prioritet</label>
                <input
                  type="number"
                  name="priority"
                  min="0"
                  step="1"
                  value={formData.priority}
                  onChange={handleChange}
                  className={inputClass}
                />
                <p className="mt-1 text-xs text-gray-500">
                  Vektet sannsynlighet. Høyere = vises oftere (f.eks. 10 får ti ganger så mange visninger som 1).
                </p>
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-3 mb-3">
                  <input
                    type="checkbox"
                    name="active"
                    checked={formData.active}
                    onChange={handleChange}
                    className="w-4 h-4 rounded bg-slate-700 border-slate-600 text-teal-500 focus:ring-teal-500"
                  />
                  <span className="text-sm text-gray-300">Aktiv (synlig for besøkende)</span>
                </label>
              </div>
            </div>
          </div>

          <div className="flex gap-3 mt-8">
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-3 bg-teal-500 text-slate-900 font-bold rounded-xl hover:bg-teal-400 disabled:opacity-50 transition-colors"
            >
              {saving ? 'Lagrer...' : editingId ? 'Oppdater annonse' : 'Lagre annonse'}
            </button>
            {editingId && (
              <button
                type="button"
                onClick={resetForm}
                className="px-6 py-3 bg-slate-700 text-gray-300 font-bold rounded-xl hover:bg-slate-600 transition-colors"
              >
                Avbryt
              </button>
            )}
          </div>
        </form>

        {/* Existing ads */}
        <div>
          <h2 className="text-xl font-bold text-white mb-4">
            Eksisterende annonser ({ads.length})
          </h2>
          <div className="space-y-3">
            {ads.map(ad => (
              <div
                key={ad.id}
                className="bg-slate-800 bg-opacity-60 border border-slate-700 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-4"
              >
                <div className="w-full sm:w-40 aspect-video bg-slate-700 rounded-lg overflow-hidden flex-shrink-0 relative">
                  <img
                    src={ad.imageUrl}
                    alt={ad.title}
                    className="w-full h-full object-cover"
                    onError={e => { e.target.style.display = 'none'; }}
                  />
                  <span className="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-amber-400 text-slate-900">
                    Annonse
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <h3 className="text-white font-semibold truncate">{ad.title}</h3>
                    {!ad.active && (
                      <span className="px-2 py-0.5 bg-yellow-500 bg-opacity-20 text-yellow-400 text-xs rounded-full flex-shrink-0">
                        Inaktiv
                      </span>
                    )}
                    <span className="px-2 py-0.5 bg-slate-700 text-gray-300 text-xs rounded-full flex-shrink-0">
                      Prio {ad.priority}
                    </span>
                  </div>
                  {ad.linkUrl ? (
                    <a
                      href={ad.linkUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-teal-400 hover:text-teal-300 underline break-all"
                    >
                      {ad.linkUrl}
                    </a>
                  ) : (
                    <span className="text-sm text-gray-500 italic">Ingen lenke (vises uten klikk)</span>
                  )}
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <button
                    onClick={() => toggleActive(ad)}
                    className="px-3 py-2 bg-slate-700 text-gray-300 rounded-lg hover:bg-slate-600 transition-colors text-sm"
                  >
                    {ad.active ? 'Sett inaktiv' : 'Aktivér'}
                  </button>
                  <button
                    onClick={() => handleEdit(ad)}
                    className="px-3 py-2 bg-slate-700 text-gray-300 rounded-lg hover:bg-slate-600 transition-colors text-sm"
                  >
                    Rediger
                  </button>
                  <button
                    onClick={() => handleDelete(ad.id)}
                    className="px-3 py-2 bg-red-500 bg-opacity-20 text-red-400 rounded-lg hover:bg-opacity-30 transition-colors text-sm"
                  >
                    Slett
                  </button>
                </div>
              </div>
            ))}

            {ads.length === 0 && (
              <p className="text-gray-500 text-center py-8">
                Ingen annonser ennå. Opprett den første ovenfor.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdsAdmin;
